import cx from "classnames";
import { CSSProperties, useEffect, useLayoutEffect } from "react";

import type { FrameCallback } from "../../render/Renderer";
import {
  SceneAndCamera,
  useSecondaryRenderer
} from "../../render/SecondaryRenderer";
import "./SecondaryRenderCanvas.css";
import { useMeasureSize } from "./useMeasureSize";

export type SecondaryRenderCanvasProps = SceneAndCamera & {
  className?: string;
  style?: CSSProperties;
  onFrame?: FrameCallback;
};

export function SecondaryRenderCanvas({
  scene,
  camera,
  className,
  style,
  onFrame
}: SecondaryRenderCanvasProps) {
  const [containerRef, containerSize] = useMeasureSize();
  const [canvasRef, binding] = useSecondaryRenderer();

  useLayoutEffect(() => {
    if (!canvasRef.current) return;
    if (!containerSize.width || !containerSize.height) return;

    canvasRef.current.width = containerSize.width;
    canvasRef.current.height = containerSize.height;
    canvasRef.current.style.setProperty("width", `${containerSize.width}px`);
    canvasRef.current.style.setProperty("height", `${containerSize.height}px`);

    binding.renderFrame();
  }, [canvasRef, binding, containerSize]);

  useLayoutEffect(() => {
    if (!scene || !camera) return;

    binding.setScene(scene).setCamera(camera);
    binding.renderFrame();
  }, [binding, scene, camera]);

  useEffect(() => {
    if (!onFrame) return;

    binding.startRenderLoop(onFrame);
    return () => binding.stopRenderLoop();
  }, [binding, onFrame]);

  return (
    <div ref={containerRef} className="secondary-render-canvas-container">
      <canvas
        ref={canvasRef}
        className={cx("secondary-render-canvas", className)}
        style={style}
      />
    </div>
  );
}
